import { Logic } from '~common/ui-logic/logic'
import { executeTask } from '~common/ui-logic/tasks'
import {
    InvoiceMetadataDisplayProps,
    InvoiceMetadataDisplayState,
} from './types'
import { normalizeDateFromFreeText } from '~common/utils/normalizeDateFromFreeText/normalizeDateFromFreeText'
import type { InvoiceDataExtractionResult } from '~common/invoice-processing/types'

export class InvoiceMetadataDisplayLogic extends Logic<
    InvoiceMetadataDisplayProps,
    InvoiceMetadataDisplayState
> {
    getInitialState(): InvoiceMetadataDisplayState {
        return {
            loadState: 'pristine',
            metadata: null,
            error: null,
            showUpdateMatches: false,
            updateMatchesState: 'pristine',
        }
    }

    async initialize(): Promise<void> {
        if (this.deps.invoiceId) {
            await this.loadInvoiceMetadata()
        }
    }

    shouldReload(
        oldDeps: InvoiceMetadataDisplayProps,
        newDeps: InvoiceMetadataDisplayProps,
    ): boolean {
        return oldDeps.invoiceId !== newDeps.invoiceId
    }

    private async loadInvoiceMetadata(): Promise<void> {
        await executeTask(this, 'loadState', async () => {
            try {
                const document =
                    await this.deps.storageModules.emailInvoice.getDocumentById(
                        this.deps.invoiceId,
                    )

                if (document && document.metadata) {
                    this.setState({
                        metadata: document.metadata,
                        error: null,
                    })
                } else {
                    this.setState({
                        metadata: null,
                        error: 'Invoice not found',
                    })
                }
            } catch (error) {
                console.error('Error loading invoice metadata:', error)
                this.setState({
                    metadata: null,
                    error:
                        error instanceof Error
                            ? error.message
                            : 'Failed to load invoice metadata',
                })
            }
        })
    }

    async updateField(
        field: keyof InvoiceDataExtractionResult | 'reason',
        value: string,
    ): Promise<void> {
        if (!this.state.metadata) {
            return
        }
        this.setState({
            showUpdateMatches: false,
        })

        await executeTask(this, 'loadState', async () => {
            try {
                let parsedValue: string | number | null | undefined

                switch (field) {
                    case 'date': {
                        const timestamp = normalizeDateFromFreeText(value, {
                            country:
                                this.state.metadata?.vendorCountry ?? undefined,
                            currency:
                                this.state.metadata?.currency ?? undefined,
                        })
                        parsedValue = timestamp ?? null
                        break
                    }
                    case 'amount':
                    case 'vatRate':
                    case 'vatAmount': {
                        const numValue = parseFloat(value)
                        parsedValue = isNaN(numValue) ? null : numValue
                        break
                    }
                    case 'vendorName':
                    case 'vendorAddress':
                    case 'vendorCountry':
                    case 'invoiceId':
                    case 'creditCard':
                    case 'currency':
                    case 'reason': {
                        parsedValue = value.trim() || null
                        break
                    }
                    default:
                        return
                }

                const updateData: {
                    [K in keyof InvoiceDataExtractionResult]?: K extends 'date'
                        ? number | null
                        : InvoiceDataExtractionResult[K] | null
                } & {
                    reason?: string | null
                } = {}

                if (field === 'date') {
                    updateData.date = parsedValue as number | null
                } else if (field === 'reason') {
                    updateData.reason = parsedValue as string | null
                } else {
                    ;(updateData as Record<string, unknown>)[field] =
                        parsedValue as string | number | null
                }

                await this.deps.storageModules.emailInvoice.updateExtractedInvoiceData(
                    {
                        invoiceId: this.deps.invoiceId,
                        metadata:
                            updateData as unknown as Partial<InvoiceDataExtractionResult>,
                    },
                )

                const updatedDocument =
                    await this.deps.storageModules.emailInvoice.getDocumentById(
                        this.deps.invoiceId,
                    )

                if (updatedDocument) {
                    await this.deps.services.cache.updateInvoice(
                        this.deps.invoiceId,
                        {
                            metadata: updatedDocument.metadata,
                            updatedAt: updatedDocument.updatedAt,
                        },
                    )

                    this.setState({
                        metadata: updatedDocument.metadata,
                        error: null,
                        showUpdateMatches: true,
                    })
                }
            } catch (error) {
                console.error(`Error updating ${field}:`, error)
                this.setState({
                    error:
                        error instanceof Error
                            ? error.message
                            : `Failed to update ${field}`,
                })
            }
        })
    }

    async updateMatches(): Promise<void> {
        await executeTask(this, 'updateMatchesState', async () => {
            try {
                if (this.deps.onUpdateMatches) {
                    await this.deps.onUpdateMatches('running')
                }
                await this.deps.services.transactionInvoiceMatching.findMatchesForInvoice(
                    this.deps.invoiceId,
                    true,
                )
                if (this.deps.onUpdateMatches) {
                    await this.deps.onUpdateMatches('success')
                }
            } catch (error) {
                console.error('Error updating matches:', error)
                this.setState({
                    error:
                        error instanceof Error
                            ? error.message
                            : 'Failed to update matches',
                })
            }
        })
    }
}
